// frontend/src/pages/AccessibilityStatement.js
//
// Hebrew accessibility statement (הצהרת נגישות) for leadifyai.biz/accessibility.
//
// Design: on-brand redesign matching the marketing site (teal/mint gradient
// hero, Heebo headings, rounded white section cards, contrast-safe dark
// contact card). RTL, semantic h1->h2->h3, decorative icons aria-hidden,
// visible focus rings, mobile-first.
//
// Accuracy: the feature list, keyboard shortcut (Alt+A) and technologies
// describe the LIVE accessibility panel (SimpleAccessibilitySettings, opened
// from the navbar menu / Alt+A), NOT the unused SimpleAccessibilityWidget or
// AccessibilityContext.
//
// >>> OWNER-EDITABLE LEGAL CONTENT lives in the `statement` constant below.
//     Two fields are gated on owner confirmation (see inline OWNER notes):
//       - conformance   (which standard/level we truthfully claim)
//       - coordinator    (named person + phone are legally required in IL)

import React from "react";
import { Box, Container, Typography, Stack, Link } from "@mui/material";
import {
  Accessibility,
  ALargeSmall,
  Contrast,
  Link2,
  ZapOff,
  AlignJustify,
  Keyboard,
  Ear,
  SkipForward,
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
  Info,
} from "lucide-react";
import Seo from "../components/Seo";
import { colors } from "../theme/tokens";

const FONT_HEAD = '"Heebo", sans-serif';
// "Assistant" was never loaded (see components/legal/LegalDocument.js) - it always
// resolved to Heebo. Dropping the name changes nothing that renders.
const FONT_BODY = '"Heebo", sans-serif';

// Icons render in the accessible dark-teal (#005A52) so they read clearly on
// the tinted chips even though they are decorative (aria-hidden).
const ICON = colors.primary.accessible;

const statement = {
  eyebrow: "נגישות",
  title: "הצהרת נגישות",
  subtitle:
    "אנו ב-Leadify AI רואים בהנגשת האתר ערך ומחויבות, ופועלים כדי שכל אדם - לרבות אנשים עם מוגבלות - יוכל לגלוש ולהשתמש בשירות בצורה נוחה, עצמאית ושוויונית.",
  // עדכון אחרון - נגזר ממועד העדכון האמיתי של ההצהרה.
  lastUpdated: "עודכן לאחרונה: 5 ביולי 2026",

  // נוסח מנוסח כ"יישום שוטף מתוך בחירה" (ולא כ"עמידה מבוקרת"): ת"י 5568 מבוסס
  // על WCAG 2.0 ברמה AA. OWNER OPTIONAL: ניתן להוסיף כאן משפט המציין שהאתר
  // מופעל על ידי עוסק פטור והנגשתו בוצעה באופן וולונטרי מעבר לנדרש - אמרו אם
  // תרצו שאוסיף זאת (חושף את סטטוס העסק, ולכן לא הוספתי מיוזמתי).
  conformance:
    'אנו ב-Leadify AI פועלים להנגשת האתר מתוך מחויבות לשוויון, תוך יישום עקרונות תקן הנגישות הישראלי (ת"י 5568) ברמה AA, המבוססים על הנחיות WCAG 2.0. אנו ממשיכים לשפר את רמת הנגישות של האתר באופן שוטף.',

  features: [
    {
      Icon: ALargeSmall,
      title: "התאמת גודל הטקסט",
      desc: "הגדלה או הקטנה של גודל הגופן (קטן / רגיל / גדול) לקריאה נוחה יותר.",
    },
    {
      Icon: Contrast,
      title: "מצב ניגודיות גבוהה",
      desc: "הגברת הניגודיות בין הטקסט לרקע לשיפור הראות.",
    },
    {
      Icon: Link2,
      title: "הדגשת קישורים",
      desc: "קו תחתון והדגשה של כל הקישורים באתר לזיהוי קל יותר.",
    },
    {
      Icon: ZapOff,
      title: "עצירת אנימציות",
      desc: "ביטול תנועות ומעברים באתר עבור משתמשים הרגישים לתנועה.",
    },
    {
      Icon: AlignJustify,
      title: "הגדלת ריווח הטקסט",
      desc: "הוספת מרווח בין שורות ואותיות לשיפור הקריאוּת.",
    },
    {
      Icon: SkipForward,
      title: "דילוג לתוכן הראשי",
      desc: "קישור המאפשר דילוג ישיר לתוכן העיקרי בעת ניווט במקלדת.",
    },
    {
      Icon: Keyboard,
      title: "ניווט מלא במקלדת",
      desc: "ניתן להפעיל את כל הרכיבים האינטראקטיביים באמצעות המקלדת בלבד.",
    },
    {
      Icon: Ear,
      title: "תאימות לקוראי מסך",
      desc: "מבנה סמנטי והכרזות קוליות המותאמים לקוראי מסך נפוצים.",
    },
  ],

  shortcuts: [
    { keys: "Alt + A", action: "פתיחה/סגירה של לוח הגדרות הנגישות" },
    { keys: "Esc", action: "סגירת לוח ההגדרות והחלונות" },
    { keys: "Tab", action: "מעבר בין הרכיבים האינטראקטיביים" },
    { keys: "Enter", action: "הפעלת כפתורים וקישורים" },
  ],

  technologies:
    "האתר פותח בטכנולוגיות אינטרנט מודרניות (HTML5 סמנטי, תכונות ARIA, ורכיבי React נגישים) המאפשרות לטכנולוגיות מסייעות - ובהן קוראי מסך - לפרש את התוכן בצורה תקינה. הניווט באתר אפשרי באמצעות עכבר, מקלדת ומסך מגע.",

  // OWNER: אם קיימים חלקים ידועים שאינם נגישים במלואם, פרטו אותם כאן יחד עם
  // הדרך החלופית לקבלת השירות. אם אין - נשאיר את המשפט הכללי.
  limitations:
    "אנו פועלים באופן שוטף לשיפור נגישות האתר. ייתכן שחלקים מסוימים באתר, או תכנים של צד שלישי, טרם הונגשו במלואם. אם נתקלתם ברכיב שאינו נגיש, נשמח שתפנו אלינו ונעשה כל מאמץ לספק לכם את המידע או השירות בערוץ חלופי בהקדם.",

  feedbackTitle: "פנייה בנושא נגישות",
  feedbackText:
    "אם נתקלתם בקושי או בבעיית נגישות באתר, נשמח לשמוע. פנו אל רכז הנגישות שלנו ואנו נטפל בפנייתכם בהקדם האפשרי:",

  coordinator: {
    name: "צוות הנגישות של Leadify AI",
    role: "רכז נגישות",
    email: "support@leadifyai.biz",
    phone: "052-436-1818",
    address: "תל אביב, ישראל",
  },
  responseTime: "אנו שואפים להשיב לפניות בנושא נגישות בתוך 2 ימי עסקים.",
};

const focusRing = {
  "&:focus-visible": {
    outline: `2px solid ${colors.primary.accessible}`,
    outlineOffset: "2px",
    borderRadius: "6px",
  },
};

const SectionCard = ({ children, sx }) => (
  <Box
    component="section"
    sx={{
      background: "#FFFFFF",
      border: `1px solid ${colors.primary.alpha12}`,
      borderRadius: "18px",
      boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
      p: { xs: 3, md: 4 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

const SectionHeading = ({ Icon, children }) => (
  <Typography
    variant="h2"
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      fontSize: { xs: "20px", md: "24px" },
      fontWeight: 800,
      fontFamily: FONT_HEAD,
      color: "#1A1A2E",
      mb: 2.5,
    }}
  >
    <Box
      aria-hidden="true"
      sx={{
        width: 40,
        height: 40,
        borderRadius: "12px",
        background: colors.primary.alpha08,
        border: `1px solid ${colors.primary.alpha18}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={20} color={ICON} strokeWidth={1.75} />
    </Box>
    {children}
  </Typography>
);

const AccessibilityStatement = () => {
  const t = statement;

  return (
    <>
      <Seo
        title="הצהרת נגישות | Leadify AI"
        description={
          'הצהרת הנגישות של Leadify AI - מחויבות לנגישות דיגיטלית בהתאם לתקן הישראלי ת"י 5568 ולהנחיות WCAG 2.0 ברמה AA, אמצעי הנגישות באתר ודרכי פנייה לרכז הנגישות.'
        }
        path="/accessibility"
        lang="he"
      />

      <Box sx={{ direction: "rtl", textAlign: "right", pb: { xs: 6, md: 10 } }}>
        {/* Hero band */}
        <Box
          sx={{
            background:
              "linear-gradient(150deg, #EDFAF8 0%, #F8FDFF 45%, #FDF3F7 100%)",
            py: { xs: 6, md: 9 },
            px: 2,
          }}
        >
          <Container maxWidth="md" sx={{ textAlign: "center" }}>
            <Box
              aria-hidden="true"
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2.5,
                borderRadius: "18px",
                background: colors.primary.alpha10,
                border: `1px solid ${colors.primary.alpha20}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Accessibility size={32} color={ICON} strokeWidth={1.75} />
            </Box>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "0.14em",
                color: colors.primary.accessible,
                fontFamily: FONT_HEAD,
                mb: 1.5,
              }}
            >
              {t.eyebrow}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "32px", sm: "40px", md: "46px" },
                fontWeight: 800,
                fontFamily: FONT_HEAD,
                color: "#1A1A2E",
                lineHeight: 1.15,
                mb: 2,
              }}
            >
              {t.title}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "15px", md: "17px" },
                fontFamily: FONT_BODY,
                color: "#4A5568",
                lineHeight: 1.75,
                maxWidth: "620px",
                mx: "auto",
              }}
            >
              {t.subtitle}
            </Typography>

            {/* Status pills */}
            <Stack
              direction="row"
              spacing={0}
              sx={{ flexWrap: "wrap", justifyContent: "center", gap: 1, mt: 3.5 }}
            >
              {['תקן ישראלי ת"י 5568', "WCAG 2.0 AA", t.lastUpdated].map(
                (label, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: 1.75,
                      py: 0.75,
                      borderRadius: "999px",
                      background: "rgba(255,255,255,0.7)",
                      border: `1px solid ${colors.primary.alpha20}`,
                      fontSize: "12.5px",
                      fontWeight: 600,
                      fontFamily: FONT_BODY,
                      color: "#3A4A5E",
                    }}
                  >
                    {label}
                  </Box>
                )
              )}
            </Stack>
          </Container>
        </Box>

        {/* Body */}
        <Container maxWidth="md" sx={{ mt: { xs: -3, md: -4 } }}>
          <Stack spacing={{ xs: 2.5, md: 3 }}>
            {/* Commitment / compliance */}
            <SectionCard>
              <SectionHeading Icon={ShieldCheck}>מחויבות ותאימות</SectionHeading>
              <Typography
                sx={{
                  fontSize: "15.5px",
                  fontFamily: FONT_BODY,
                  color: "#3A4A5E",
                  lineHeight: 1.85,
                }}
              >
                {t.conformance}
              </Typography>
            </SectionCard>

            {/* Features grid */}
            <SectionCard>
              <SectionHeading Icon={Accessibility}>
                אמצעי הנגישות באתר
              </SectionHeading>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: { xs: 1.5, md: 2 },
                }}
              >
                {t.features.map((f, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      p: 2,
                      borderRadius: "14px",
                      background: colors.primary.alpha04,
                      border: `1px solid ${colors.primary.alpha08}`,
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "10px",
                        background: "#FFFFFF",
                        border: `1px solid ${colors.primary.alpha18}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <f.Icon size={18} color={ICON} strokeWidth={1.75} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: "15px",
                          fontWeight: 700,
                          fontFamily: FONT_HEAD,
                          color: "#1A1A2E",
                          mb: 0.4,
                        }}
                      >
                        {f.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "13.5px",
                          fontFamily: FONT_BODY,
                          color: "#5A6A7E",
                          lineHeight: 1.6,
                        }}
                      >
                        {f.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Typography
                sx={{
                  mt: 2.5,
                  fontSize: "14px",
                  fontFamily: FONT_BODY,
                  color: "#5A6A7E",
                  lineHeight: 1.75,
                }}
              >
                את לוח הגדרות הנגישות ניתן לפתוח מתפריט הניווט של האתר, ובכל
                עמוד באמצעות קיצור המקלדת <strong>Alt + A</strong>.
              </Typography>
            </SectionCard>

            {/* Keyboard shortcuts */}
            <SectionCard>
              <SectionHeading Icon={Keyboard}>קיצורי מקלדת</SectionHeading>
              <Stack spacing={1}>
                {t.shortcuts.map((s, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1,
                      borderBottom:
                        i < t.shortcuts.length - 1
                          ? `1px solid ${colors.primary.alpha08}`
                          : "none",
                    }}
                  >
                    <Box
                      component="kbd"
                      dir="ltr"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#1A1A2E",
                        background: colors.primary.alpha08,
                        border: `1px solid ${colors.primary.alpha20}`,
                        borderRadius: "8px",
                        px: 1.25,
                        py: 0.5,
                        minWidth: 92,
                        textAlign: "center",
                        flexShrink: 0,
                      }}
                    >
                      {s.keys}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "14.5px",
                        fontFamily: FONT_BODY,
                        color: "#3A4A5E",
                      }}
                    >
                      {s.action}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </SectionCard>

            {/* Technologies */}
            <SectionCard>
              <SectionHeading Icon={Info}>הטכנולוגיות שבבסיס האתר</SectionHeading>
              <Typography
                sx={{
                  fontSize: "15.5px",
                  fontFamily: FONT_BODY,
                  color: "#3A4A5E",
                  lineHeight: 1.85,
                }}
              >
                {t.technologies}
              </Typography>
            </SectionCard>

            {/* Known limitations */}
            <SectionCard>
              <SectionHeading Icon={Info}>מגבלות ידועות</SectionHeading>
              <Typography
                sx={{
                  fontSize: "15.5px",
                  fontFamily: FONT_BODY,
                  color: "#3A4A5E",
                  lineHeight: 1.85,
                }}
              >
                {t.limitations}
              </Typography>
            </SectionCard>

            {/* Contact - contrast-safe dark card */}
            <Box
              component="section"
              sx={{
                borderRadius: "18px",
                p: { xs: 3, md: 4 },
                background: `linear-gradient(150deg, ${colors.surface.deep} 0%, ${colors.surface.edge} 100%)`,
                color: colors.surface.onDark,
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "20px", md: "24px" },
                  fontWeight: 800,
                  fontFamily: FONT_HEAD,
                  color: "#FFFFFF",
                  mb: 1.5,
                }}
              >
                {t.feedbackTitle}
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontFamily: FONT_BODY,
                  color: colors.surface.onDark,
                  lineHeight: 1.8,
                  mb: 3,
                }}
              >
                {t.feedbackText}
              </Typography>

              <Typography
                variant="h3"
                sx={{
                  fontSize: "15px",
                  fontWeight: 700,
                  fontFamily: FONT_HEAD,
                  color: "#FFFFFF",
                  mb: 0.5,
                }}
              >
                {t.coordinator.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "13.5px",
                  fontFamily: FONT_BODY,
                  color: colors.surface.onDarkMuted,
                  mb: 2,
                }}
              >
                {t.coordinator.role}
              </Typography>

              <Stack spacing={1.25}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Mail aria-hidden="true" size={18} color={colors.primary.light} strokeWidth={1.75} />
                  <Link
                    href={`mailto:${t.coordinator.email}`}
                    dir="ltr"
                    sx={{
                      color: colors.primary.light,
                      fontFamily: FONT_BODY,
                      fontSize: "15px",
                      fontWeight: 600,
                      textDecoration: "underline",
                      ...focusRing,
                    }}
                  >
                    {t.coordinator.email}
                  </Link>
                </Box>
                {t.coordinator.phone && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Phone aria-hidden="true" size={18} color={colors.primary.light} strokeWidth={1.75} />
                    <Link
                      href={`tel:${t.coordinator.phone.replace(/[^\d+]/g, "")}`}
                      dir="ltr"
                      sx={{
                        color: colors.primary.light,
                        fontFamily: FONT_BODY,
                        fontSize: "15px",
                        fontWeight: 600,
                        textDecoration: "underline",
                        ...focusRing,
                      }}
                    >
                      {t.coordinator.phone}
                    </Link>
                  </Box>
                )}
                {t.coordinator.address && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <MapPin aria-hidden="true" size={18} color={colors.surface.onDarkMuted} strokeWidth={1.75} />
                    <Typography
                      sx={{
                        fontSize: "14.5px",
                        fontFamily: FONT_BODY,
                        color: colors.surface.onDark,
                      }}
                    >
                      {t.coordinator.address}
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Typography
                sx={{
                  mt: 3,
                  fontSize: "13.5px",
                  fontFamily: FONT_BODY,
                  color: colors.surface.onDarkMuted,
                  fontStyle: "italic",
                }}
              >
                {t.responseTime}
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default AccessibilityStatement;
